import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  submitContactForm,
  validateEmail, 
  validatePhone, 
  sanitizeInput
} from '../../../services/backendService';

const ContactForm = ({ 
  pageSource = 'Website', 
  customStyles = {},
  showTitle = true,
  title = "Get In Touch",
  onSubmitSuccess,
  onSubmitError 
}) => {
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: ''
  });

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [errors, setErrors] = useState({});
  const [showNotification, setShowNotification] = useState(false);


  // Default styles with override capability
  const defaultStyles = {
    section: {
      background: 'linear-gradient(135deg, #E67E22 0%, #D35400 100%)',
      color: 'white',
      padding: '6rem 0',
      textAlign: 'center'
    },
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '0 2rem'
    },
    title: {
      fontSize: '3rem',
      fontWeight: '700',
      margin: '0 0 3rem 0',
      textTransform: 'uppercase'
    },
    form: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '2rem'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column'
    },
    label: {
      fontSize: '1rem',
      marginBottom: '0.5rem',
      textAlign: 'left',
      opacity: '0.9'
    },
    input: {
      padding: '1rem 0',
      border: 'none',
      borderBottom: '2px solid rgba(255,255,255,0.3)',
      backgroundColor: 'transparent',
      color: 'white',
      fontSize: '1rem',
      outline: 'none',
      transition: 'border-bottom-color 0.3s ease'
    },
    inputError: {
      borderBottomColor: '#ff6b6b'
    },
    textareaGroup: {
      gridColumn: 'span 2',
      display: 'flex',
      flexDirection: 'column'
    },
    textarea: {
      padding: '1rem 0',
      border: 'none',
      borderBottom: '2px solid rgba(255,255,255,0.3)',
      backgroundColor: 'transparent',
      color: 'white',
      fontSize: '1rem',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
      fontFamily: 'inherit'
    },
    submitButton: {
      gridColumn: 'span 2',
      marginTop: '2rem',
      padding: '1rem 3rem',
      backgroundColor: 'transparent',
      border: '2px solid white',
      color: 'white',
      borderRadius: '25px',
      fontSize: '1.1rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden'
    },
    submitButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
    error: {
      color: '#ff6b6b',
      fontSize: '0.8rem',
      marginTop: '0.5rem',
      textAlign: 'left'
    }
  };

  const styles = { ...defaultStyles, ...customStyles };

  // Validate single field
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return value.length < 2 ? 'Name must be at least 2 characters' : '';
      case 'email':
        return !validateEmail(value) ? 'Please enter a valid email address' : '';
      case 'phone':
        return value && !validatePhone(value) ? 'Please enter a valid phone number' : '';
      case 'message':
        return value.length < 10 ? 'Message must be at least 10 characters' : '';
      default:
        return '';
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = name === 'message' ? value : sanitizeInput(value);
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};
    
    Object.keys(formData).forEach(key => {
      if (key !== 'address') { // Address is optional
        const error = validateField(key, formData[key]);
        if (error) newErrors[key] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await submitContactForm(formData);

      if (result.success) {
        setSubmitStatus('success');
        setShowNotification(true);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          message: ''
        });

        // Call success callback if provided
        if (onSubmitSuccess) {
          onSubmitSuccess(result);
        }

        // Hide notification after 5 seconds
        setTimeout(() => {
          setShowNotification(false);
        }, 5000);

      } else {
        throw new Error(result.message);
      }

    } catch (error) {
      setSubmitStatus('error');
      setShowNotification(true);

      // Call error callback if provided
      if (onSubmitError) {
        onSubmitError(error);
      }

      // Hide notification after 5 seconds
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        .contact-input::placeholder,
        .contact-textarea::placeholder {
          color: rgba(255,255,255,0.7);
        }
        
        .contact-input:focus,
        .contact-textarea:focus {
          border-bottom-color: white !important;
        }
        
        .submit-btn:hover:not(:disabled) {
          background-color: white;
          color: #E67E22;
        }
        
        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .loading-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .notification-message {
          animation: slideInUp 0.3s ease-out;
        }
        
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @media (max-width: 768px) {
          .contact-form-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <section style={styles.section}>
        <div style={styles.container}>
          {showTitle && (
            <h2 style={styles.title}>{title}</h2>
          )}
          
          {/* Success/Error Notification - Positioned within form */}
          <AnimatePresence>
            {showNotification && (
              <motion.div
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="notification-message"
                style={{
                  backgroundColor: submitStatus === 'success' ? 'rgba(16, 185, 129, 0.95)' : 'rgba(239, 68, 68, 0.95)',
                  color: 'white',
                  padding: '1.2rem 2rem',
                  borderRadius: '12px',
                  marginBottom: '2rem',
                  textAlign: 'center',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${submitStatus === 'success' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.2)'}`,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }}
              >
                <div style={{ fontWeight: '600', marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                  {submitStatus === 'success' ? '✅ Message Sent Successfully!' : '❌ Failed to Send Message'}
                </div>
                <div style={{ fontSize: '0.95rem', opacity: 0.9, lineHeight: '1.4' }}>
                  {submitStatus === 'success' 
                    ? 'Thank you for contacting SiriDharini Farmlands. We will get back to you soon!'
                    : 'Please try again or contact us directly. We apologize for the inconvenience.'
                  }
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          <form onSubmit={handleSubmit} style={styles.form} className="contact-form-grid">
            <div style={styles.inputGroup}>
              <label style={styles.label}>Name *</label>
              <input
                style={{
                  ...styles.input,
                  ...(errors.name ? styles.inputError : {})
                }}
                className="contact-input"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your full name"
                required
              />
              {errors.name && <div style={styles.error}>{errors.name}</div>}
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Email *</label>
              <input
                style={{
                  ...styles.input,
                  ...(errors.email ? styles.inputError : {})
                }}
                className="contact-input"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
                required
              />
              {errors.email && <div style={styles.error}>{errors.email}</div>}
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Phone</label>
              <input
                style={{
                  ...styles.input,
                  ...(errors.phone ? styles.inputError : {})
                }}
                className="contact-input"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+91 9876543210"
              />
              {errors.phone && <div style={styles.error}>{errors.phone}</div>}
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Address</label>
              <input
                style={styles.input}
                className="contact-input"
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Your address (optional)"
              />
            </div>

            <div style={styles.textareaGroup}>
              <label style={styles.label}>Message *</label>
              <textarea
                style={{
                  ...styles.textarea,
                  ...(errors.message ? styles.inputError : {})
                }}
                className="contact-textarea"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us about your requirements..."
                required
              />
              {errors.message && <div style={styles.error}>{errors.message}</div>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                ...styles.submitButton,
                ...(isSubmitting ? styles.submitButtonDisabled : {})
              }}
              className="submit-btn"
            >
              {isSubmitting ? (
                <>
                  <div className="loading-spinner" style={{ marginRight: '0.5rem' }} />
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

export default ContactForm;